import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../services/api';
import { cachedGet, invalidateCachedGet } from '../../services/requestCache';
import NoClientState from './NoClientState';
import './ai-vis.css';

/* ── Multi-value cell ───────────────────────────────────────────────────── */
function MultiValueCell({ raw }) {
  if (!raw) return <span className="aivis-text-muted">—</span>;
  const items = raw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  if (items.length === 0) return <span className="aivis-text-muted">—</span>;
  const rest = items.length - 1;
  return (
    <span className="aivis-comp-multival">
      <span className="aivis-comp-multival-first" title={items.join(', ')}>{items[0]}</span>
      {rest > 0 && (
        <span className="aivis-comp-multival-more" title={items.slice(1).join(', ')}>+{rest}</span>
      )}
    </span>
  );
}

/* ── Sort icon ───────────────────────────────────────────────────────────── */
function SortIcon({ active, dir }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden className={`aivis-comp-sort-icon${active ? ' aivis-comp-sort-icon--active' : ''}`}>
      <path d="M3.5 5L6 2L8.5 5H3.5Z" fill="currentColor" opacity={active && dir === 'asc' ? 1 : 0.35} />
      <path d="M3.5 7L6 10L8.5 7H3.5Z" fill="currentColor" opacity={active && dir === 'desc' ? 1 : 0.35} />
    </svg>
  );
}

function SortHeader({ label, sortKey, currentSort, currentDir, onSort, numeric }) {
  const active = currentSort === sortKey;
  return (
    <th
      className={`aivis-comp-th-sortable${numeric ? ' aivis-comp-th-num' : ''}`}
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (currentDir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <span className="aivis-comp-th-inner">
        {label}
        <SortIcon active={active} dir={currentDir} />
      </span>
    </th>
  );
}

/* ── Logo helper ─────────────────────────────────────────────────────────── */
function BrandLogo({ logoUrl, displayName, size = 36 }) {
  const [failed, setFailed] = useState(false);

  // Reset failure state if the URL changes
  useEffect(() => { setFailed(false); }, [logoUrl]);

  const initial = (displayName || '?')[0].toUpperCase();

  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        alt={initial}
        width={size}
        height={size}
        className="aivis-comp-logo-img"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className="aivis-comp-logo-fallback" style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {initial}
    </span>
  );
}

/* ── Remove competitor confirmation modal ───────────────────────────────── */
function RemoveCompetitorModal({ displayName, onConfirm, onCancel }) {
  if (!displayName) return null;
  return (
    <div
      className="aivis-modal-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="aivis-modal aivis-org-modal" style={{ maxWidth: 420 }}>
        <div className="aivis-modal-head">
          <h2 className="aivis-modal-title">Remove Competitor</h2>
        </div>
        <p style={{ margin: '16px 0 8px', color: 'var(--aivis-text-muted)', fontSize: 14, lineHeight: 1.6 }}>
          Are you sure you want to remove{' '}
          <strong style={{ color: 'var(--aivis-text, #111)' }}>{displayName}</strong>{' '}
          from competitors?
          <br />This will permanently delete all tracking data for this brand.
        </p>
        <div className="aivis-modal-actions aivis-modal-actions--split" style={{ marginTop: 24 }}>
          <button type="button" className="aivis-btn-secondary aivis-modal-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="aivis-btn-danger aivis-modal-btn-main"
            style={{ flex: '0 0 70%' }}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Side panel ──────────────────────────────────────────────────────────── */
function CompetitorPanel({ competitor, onClose, onSaved, onDeleted, campaignId }) {
  const isNew = !competitor?.id || competitor._new;
  const [displayName, setDisplayName] = useState(competitor?.display_name || '');
  const [trackedNames, setTrackedNames] = useState(competitor?.tracked_names || '');
  const [domains, setDomains] = useState(competitor?.domains || '');
  const [logoUrl] = useState(competitor?.logo_url || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    setDisplayName(competitor?.display_name || '');
    setTrackedNames(competitor?.tracked_names || '');
    setDomains(competitor?.domains || '');
    setErr('');
    setConfirmRemove(false);
  }, [competitor]);

  async function handleSave() {
    if (!displayName.trim()) { setErr('Display name is required.'); return; }
    setBusy(true); setErr('');
    try {
      const payload = { display_name: displayName.trim(), tracked_names: trackedNames, domains };
      if (isNew) {
        const { data } = await api.post(`/aivis/campaigns/${campaignId}/competitors/`, payload);
        onSaved(data, true);
      } else {
        const { data } = await api.patch(`/aivis/campaigns/${campaignId}/competitors/${competitor.id}/`, payload);
        onSaved(data, false);
      }
    } catch (ex) {
      setErr(ex.response?.data?.detail || Object.values(ex.response?.data || {}).flat().join(' ') || 'Could not save competitor.');
    } finally { setBusy(false); }
  }

  async function handleDeleteConfirmed() {
    setConfirmRemove(false);
    setBusy(true);
    try {
      await api.delete(`/aivis/campaigns/${campaignId}/competitors/${competitor.id}/`);
      onDeleted(competitor.id);
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Could not delete competitor.');
      setBusy(false);
    }
  }

  return (
    <>
    {confirmRemove && (
      <RemoveCompetitorModal
        displayName={competitor?.display_name}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setConfirmRemove(false)}
      />
    )}
    <div className="aivis-comp-panel">
      <div className="aivis-comp-panel-head">
        <h2 className="aivis-comp-panel-title">{isNew ? 'Add Competitor' : 'Edit Competitor'}</h2>
        <button type="button" className="aivis-comp-panel-close" onClick={onClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div className="aivis-comp-panel-preview">
        <BrandLogo logoUrl={logoUrl} displayName={displayName} size={48} />
        <span className="aivis-comp-panel-preview-name">{displayName || 'Brand name'}</span>
      </div>

      <div className="aivis-field">
        <label>Display Name</label>
        <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
          placeholder='e.g. "Bearaby"' disabled={busy || competitor?.is_client} />
      </div>

      <div className="aivis-field">
        <label>Tracked Names</label>
        <textarea rows={3} value={trackedNames} onChange={(e) => setTrackedNames(e.target.value)}
          placeholder="One name or alias per line. Used to detect mentions in AI responses."
          disabled={busy} />
        <span className="aivis-field-hint">Comma or newline separated. The AI response is scanned for these names.</span>
      </div>

      <div className="aivis-field">
        <label>Domains</label>
        <textarea rows={2} value={domains} onChange={(e) => setDomains(e.target.value)}
          placeholder="e.g. bearaby.com" disabled={busy} />
        <span className="aivis-field-hint">Comma or newline separated root domains (no https://).</span>
      </div>

      {err && <p className="aivis-auth-error" style={{ marginTop: 8 }}>{err}</p>}

      <div className="aivis-comp-panel-actions">
        {!isNew && !competitor?.is_client && (
          <button type="button" className="aivis-apikey-revoke-btn" disabled={busy} onClick={() => setConfirmRemove(true)}>
            Remove
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" className="aivis-btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="aivis-btn-primary" onClick={handleSave} disabled={busy}>
          {busy ? 'Saving…' : isNew ? 'Add competitor' : 'Save changes'}
        </button>
      </div>
    </div>
    </>
  );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
export default function CompetitorsPage() {
  const { selectedCampaignId, campaigns } = useOutletContext();
  const selected = campaigns.find((c) => String(c.id) === String(selectedCampaignId));

  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [analyticsRefreshing, setAnalyticsRefreshing] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sortKey, setSortKey] = useState('visibility_pct');
  const [sortDir, setSortDir] = useState('desc');

  function handleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  }

  const load = useCallback(async () => {
    if (!selectedCampaignId) { setCompetitors([]); return; }
    setLoading(true); setError('');
    try {
      const cacheKey = `competitors:${selectedCampaignId}`;
      const data = await cachedGet(
        cacheKey,
        `/aivis/campaigns/${selectedCampaignId}/competitors/`,
        { maxAge: analyticsRefreshing ? 0 : 15000 },
      );
      setCompetitors(Array.isArray(data) ? data : (data.results ?? []));
      setAnalyticsRefreshing(Boolean(data?.refreshing));
    } catch (ex) {
      setError(ex.response?.data?.detail || 'Could not load competitors.');
    }
    setLoading(false);
  }, [selectedCampaignId, analyticsRefreshing]);

  useEffect(() => { load(); }, [load]);

  // Historical analysis is asynchronous only after a competitor definition
  // changes. Poll narrowly while it is building; normal page visits never poll.
  useEffect(() => {
    if (!analyticsRefreshing) return undefined;
    const timer = window.setTimeout(load, 5000);
    return () => window.clearTimeout(timer);
  }, [analyticsRefreshing, load]);

  const enriched = useMemo(
    () => competitors.map((c) => ({
      ...c,
      visibility_pct: typeof c.visibility_pct === 'number' ? c.visibility_pct : null,
      rank: typeof c.rank === 'number' ? c.rank : null,
    })),
    [competitors],
  );

  function openAdd() { setEditing({ _new: true }); setPanelOpen(true); }
  function openEdit(comp) { setEditing(comp); setPanelOpen(true); }
  function closePanel() { setPanelOpen(false); setEditing(null); }

  function handleSaved(comp, isNew) {
    invalidateCachedGet(`competitors:${selectedCampaignId}`);
    if (isNew) {
      setCompetitors((prev) => [...prev, comp]);
    } else {
      setCompetitors((prev) => prev.map((c) => c.id === comp.id
        ? { ...comp, mention_count: c.mention_count, rank: c.rank, visibility_pct: c.visibility_pct }
        : c
      ));
    }
    closePanel();
    window.setTimeout(load, 0);
  }

  function handleDeleted(id) {
    invalidateCachedGet(`competitors:${selectedCampaignId}`);
    setCompetitors((prev) => prev.filter((c) => c.id !== id));
    closePanel();
    window.setTimeout(load, 0);
  }

  // Sort all rows together by the selected key — true rank respected
  const sortedRows = useMemo(() => {
    const arr = [...enriched];
    arr.sort((a, b) => {
      // Null ranks / pcts always go to the bottom
      const av = a[sortKey] ?? (sortDir === 'asc' ? Infinity : -Infinity);
      const bv = b[sortKey] ?? (sortDir === 'asc' ? Infinity : -Infinity);
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return sortDir === 'asc' ? -1 : 1;
      if (as > bs) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [enriched, sortKey, sortDir]);

  const orderedRows = sortedRows;

  if (!selectedCampaignId) {
    return <NoClientState />;
  }

  return (
    <div className="aivis-comp-page">
      {/* Dimming overlay — click to close */}
      {panelOpen && (
        <div className="aivis-comp-overlay" onClick={closePanel} aria-hidden="true" />
      )}

      <header className="aivis-dash-header">
        <div>
          <h1 className="aivis-page-title">Competitors</h1>
          <p className="aivis-page-sub">
            {selected
              ? `Track competitor brands alongside "${selected.client_name}" in the Rankings and Visibility charts.`
              : 'Select a client to manage competitors.'}
          </p>
        </div>
        {selectedCampaignId && (
          <button type="button" className="aivis-comp-add-btn" onClick={openAdd}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
              <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            Add Competitor
          </button>
        )}
      </header>

      {error && <div className="aivis-auth-error" style={{ marginBottom: 16 }}>{error}</div>}
      {analyticsRefreshing && (
        <div className="aivis-page-sub" style={{ marginBottom: 16 }} role="status">
          Competitor metrics are refreshing after a change. Figures shown are from the most recent completed calculation.
        </div>
      )}

      <section className="aivis-dash-section">
        <div className="aivis-dash-card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <p className="aivis-dash-empty" style={{ padding: '32px 24px' }}>Loading…</p>
          ) : !selectedCampaignId ? (
            <p className="aivis-dash-empty" style={{ padding: '32px 24px' }}>Select a client to view competitors.</p>
          ) : orderedRows.length === 0 ? (
            <p className="aivis-dash-empty" style={{ padding: '32px 24px' }}>No competitors yet. Click "Add Competitor" to start tracking.</p>
          ) : (
            <table className="aivis-comp-table">
              <thead>
                <tr>
                  <SortHeader label="Rank" sortKey="rank" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} numeric />
                  <th className="aivis-comp-th-logo">Brand Logo</th>
                  <SortHeader label="Display name" sortKey="display_name" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Tracked names" sortKey="tracked_names" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Domains" sortKey="domains" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortHeader label="Visibility" sortKey="visibility_pct" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} numeric />
                  <SortHeader label="Mentions" sortKey="mention_count" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} numeric />
                  <th />
                </tr>
              </thead>
              <tbody>
                {orderedRows.map((comp) => (
                  <tr key={comp.id} className={comp.is_client ? 'aivis-comp-tr--client' : ''}>
                    <td className="aivis-comp-td-mentions">
                      {comp.rank != null
                        ? <span className="aivis-comp-rank-badge">#{comp.rank}</span>
                        : <span className="aivis-text-muted">—</span>}
                    </td>
                    <td>
                      <div className="aivis-comp-logo-cell">
                        <BrandLogo logoUrl={comp.logo_url} displayName={comp.display_name} size={32} />
                        {comp.brand_color && (
                          <span className="aivis-comp-color-dot" style={{ background: comp.brand_color }} title={`Brand color: ${comp.brand_color}`} />
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="aivis-comp-name-cell">
                        <span className="aivis-comp-display-name">{comp.display_name}</span>
                        {comp.is_client && <span className="aivis-comp-you-tag">You</span>}
                      </div>
                    </td>
                    <td className="aivis-comp-td-wrap">
                      <MultiValueCell raw={comp.tracked_names} />
                    </td>
                    <td className="aivis-comp-td-wrap">
                      <MultiValueCell raw={comp.domains} />
                    </td>
                    <td className="aivis-comp-td-mentions">
                      {comp.visibility_pct != null
                        ? <strong>{comp.visibility_pct}%</strong>
                        : <span className="aivis-text-muted">—</span>}
                    </td>
                    <td className="aivis-comp-td-mentions">
                      {comp.mention_count != null ? comp.mention_count.toLocaleString() : '—'}
                    </td>
                    <td>
                      <button type="button" className="aivis-comp-edit-btn" onClick={() => openEdit(comp)} aria-label="Edit">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Slide-in panel — fixed to viewport right edge, 40% width */}
      <div className={`aivis-comp-panel-drawer ${panelOpen ? 'aivis-comp-panel-drawer--open' : ''}`}>
        {panelOpen && editing && (
          <CompetitorPanel
            competitor={editing}
            campaignId={selectedCampaignId}
            onClose={closePanel}
            onSaved={handleSaved}
            onDeleted={handleDeleted}
          />
        )}
      </div>
    </div>
  );
}
